import { plainToClass } from "class-transformer";
import { LineStringFeature } from "../../../../../utils/models/LineStringFeature";
import type { Route } from "../../../../../utils/models/Route";
import {
  remapDestinationsAfterReversal,
  type StageDestination,
} from "../../../services/stage-destinations";
import { renumberDefaultNamesInPlace } from "../../../services/default-stage-names";
import type { UndoCommand, CommandContext } from "../types";

/**
 * Reverse the ENTIRE itinerary in place — used when no single day/route is
 * selected. Every route's path direction is flipped, the order of routes (by
 * position) is reversed, and day labels are mirrored (first day ↔ last day),
 * so the whole hike reads back-to-front.
 *
 * Pure and self-inverse: the set of position values and the set of distinct
 * day numbers are preserved (only reassigned), so applying it a second time
 * restores the original state. That's what lets ReverseItineraryCommand be a
 * no-data self-inverse undo command, mirroring ReverseRouteCommand.
 */
export function reverseItineraryInPlace(routes: { [key: string]: Route }): void {
  const keys = Object.keys(routes);
  if (keys.length === 0) return;

  // 1. Reverse each route's path (markers + linestrings), preserving properties.
  for (const key of keys) {
    const route = routes[key];
    route.markers.reverse();
    route.lineStrings = route.lineStrings.map(lineString => {
      const instance = plainToClass(LineStringFeature, lineString);
      return instance.reverse();
    }).reverse();
    route.updated_at = new Date();
  }

  // 2. Reverse the order of routes. Keep the same set of position values but
  //    hand them out in reverse so the last stage becomes the first.
  const ordered = keys
    .map(key => routes[key])
    .sort((a, b) => a.position - b.position);
  const positions = ordered.map(r => r.position);
  for (let i = 0; i < ordered.length; i++) {
    ordered[i].position = positions[positions.length - 1 - i];
  }

  // 3. Mirror day labels so day 1 ↔ last day. Routes with no day are left
  //    untouched. Only meaningful when the plan actually spans multiple days.
  const dayMap = dayMirrorMap(routes);
  for (const key of keys) {
    const route = routes[key];
    if (route.day !== undefined) {
      route.day = dayMap.get(route.day) ?? route.day;
    }
  }
}

/**
 * The day-number mirror a full reversal applies: first day ↔ last day, derived
 * from the distinct day numbers currently present on the routes. Empty when the
 * plan spans fewer than two days (nothing to mirror).
 *
 * An involution by construction — mirroring twice is the identity — which is
 * what lets the whole reversal stay self-inverse. Must be computed BEFORE
 * reverseItineraryInPlace runs, since that reassigns `route.day`.
 */
/** The plan's day numbers, ascending and de-duplicated. */
export function orderedDistinctDays(routes: { [key: string]: Route }): number[] {
  return [...new Set(
    Object.keys(routes).map(k => routes[k].day).filter((d): d is number => d !== undefined)
  )].sort((a, b) => a - b);
}

export function dayMirrorMap(routes: { [key: string]: Route }): Map<number, number> {
  const distinctDays = orderedDistinctDays(routes);

  const dayMap = new Map<number, number>();
  if (distinctDays.length > 1) {
    const reversedDays = [...distinctDays].reverse();
    distinctDays.forEach((d, i) => dayMap.set(d, reversedDays[i]));
  }
  return dayMap;
}

/**
 * Move the state that is keyed BY DAY NUMBER onto the numbers the mirror
 * assigns: a day's name/description, its persisted `hike_day` id, and the day
 * numbers on hike highlights. All of these describe a day's CONTENT, and
 * reversal moves that content to a different day number — so leaving them
 * behind silently relabels every stage (day 1's name ending up on what is now
 * the last day).
 *
 * The `hike_day` id travels with the entry deliberately: getHikeDaysAttributes
 * sends it back as `id`, so it decides which hike_day row receives this stage.
 * Keeping id and name together keeps each row bound to the stage it has always
 * described, rather than renaming every row on the way through.
 *
 * Day numbers no route belongs to are not part of the mirror, so their entries
 * stay put; a mirrored day wins if it lands on such a key, since that is the
 * one carrying real content.
 */
export function remapDayKeyedStateInPlace(
  state: {
    dayInfo?: { [dayNumber: number]: { name?: string; description?: string; id?: number } };
    hikeHighlights?: Array<{ dayNumber?: number | null }>;
  },
  dayMap: Map<number, number>
): void {
  if (dayMap.size === 0) return;

  const source = state.dayInfo;
  if (source) {
    const next: typeof source = {};
    // Unmapped days first, so a mirrored day overwrites a stale entry sitting
    // on its new number rather than being overwritten by it.
    Object.keys(source).forEach(key => {
      const day = Number(key);
      if (!dayMap.has(day)) next[day] = source[day];
    });
    Object.keys(source).forEach(key => {
      const day = Number(key);
      const moved = dayMap.get(day);
      if (moved !== undefined) next[moved] = source[day];
    });
    state.dayInfo = next;
  }

  state.hikeHighlights?.forEach(highlight => {
    if (typeof highlight.dayNumber !== 'number') return;
    const moved = dayMap.get(highlight.dayNumber);
    if (moved !== undefined) highlight.dayNumber = moved;
  });
}

/**
 * Reverse the itinerary AND keep stage-destination (POI/waypoint/highlight)
 * attachments intact — including the trail-start destination. Reversing
 * flips which point of the trail lands on each attached item (a day's stage
 * end can become the trail start, and vice versa for the old trail start
 * and the new last day's end), so after the geometry flip we re-key both
 * `stageDestinations` and `trailStartDestination` together onto whichever
 * anchor now sits on them — otherwise the render-time prune (which
 * validates attachment by coordinate proximity) would drop them.
 *
 * Self-inverse for the same reason reverseItineraryInPlace is: the geometry is
 * self-inverse and the items never move, so re-matching by coordinates restores
 * the original keys on a second application.
 */
export function reverseItineraryStateInPlace(
  state: {
    routes: { [routeKey: string]: Route };
    stageDestinations?: { [dayNumber: number]: StageDestination };
    trailStartDestination?: StageDestination | null;
    dayInfo?: { [dayNumber: number]: { name?: string; description?: string; id?: number } };
    hikeHighlights?: Array<{ dayNumber?: number | null }>;
  }
): void {
  // Captured before the flip — reverseItineraryInPlace reassigns route.day, so
  // afterwards the original day numbering is no longer recoverable.
  const dayMap = dayMirrorMap(state.routes);

  reverseItineraryInPlace(state.routes);
  remapDayKeyedStateInPlace(state, dayMap);

  // Auto-generated names ("Day 3: stage 3", "stage 3") are positional, so once a
  // day's content has moved they describe the wrong position. Renumber just
  // those; anything a user has renamed is left untouched.
  renumberDefaultNamesInPlace(state, dayMap);

  const hasStageDestinations = state.stageDestinations && Object.keys(state.stageDestinations).length > 0;
  if (hasStageDestinations || state.trailStartDestination) {
    const remapped = remapDestinationsAfterReversal(
      state.routes,
      state.stageDestinations || {},
      state.trailStartDestination || null
    );
    state.stageDestinations = remapped.stageDestinations;
    state.trailStartDestination = remapped.trailStartDestination;
  }
}

/** Each route's segments, by route key. */
export type LineStringsByRoute = { [routeKey: string]: any[] };

/**
 * Command for reversing the whole itinerary (all days + all routes and their
 * paths, plus stage-destination attachments).
 *
 * Order, days and destinations are self-inverse, so undo and redo both re-apply the reversal.
 * The segments are not, for the reason `ReverseRouteCommand` gives — mirroring drops what
 * cannot be inverted, and bike and car segments were routed again — so each route's segments
 * are then put back from a snapshot taken before (undo) or after (redo). Deep copies, so later
 * edits cannot reach them.
 */
export class ReverseItineraryCommand implements UndoCommand {
  readonly type = 'REVERSE_ITINERARY';
  readonly routeKey = null; // cross-route operation

  private readonly lineStringsBefore: LineStringsByRoute;
  private readonly lineStringsAfter: LineStringsByRoute;

  constructor(lineStringsBefore: LineStringsByRoute = {}, lineStringsAfter: LineStringsByRoute = {}) {
    this.lineStringsBefore = JSON.parse(JSON.stringify(lineStringsBefore));
    this.lineStringsAfter = JSON.parse(JSON.stringify(lineStringsAfter));
  }

  undo(ctx: CommandContext): void {
    reverseItineraryStateInPlace(ctx.state);
    restoreLineStrings(ctx.state.routes, this.lineStringsBefore);
  }

  redo(ctx: CommandContext): void {
    reverseItineraryStateInPlace(ctx.state);
    restoreLineStrings(ctx.state.routes, this.lineStringsAfter);
  }
}

function restoreLineStrings(routes: { [routeKey: string]: Route }, snapshot: LineStringsByRoute): void {
  Object.keys(snapshot).forEach(key => {
    if (routes[key]) routes[key].lineStrings = JSON.parse(JSON.stringify(snapshot[key]));
  });
}
